'use client';

import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { T } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { Button } from '@flipster/react/design-system/button';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { RadioButton } from '@flipster/react/design-system/radio-button';
import { TextButton } from '@flipster/react/design-system/text-button';
import { Explain } from '@flipster/react/design-utils/explain';
import Formatted from '@flipster/react/design-utils/formatted';
import XCircleIcon from '@flipster/react/icon/generated/XCircleIcon';
import { css } from '@flipster/styled-system/css';
import { grid, hstack, stack, vstack } from '@flipster/styled-system/patterns';
import { Input } from '@flipster/ui';

import { NumberInputWithSuffix } from '../../../input/number-input-with-suffix';
import { authBunja } from '../../../shared/auth/state';
import { applyMasterTraderBunja } from '../state';

const steps = [
  { step: 1, label: <T id='Copy.r260127.Submit_your_application_above' /> },
  { step: 2, label: <T id='Copy.r260127.Our_team_reviews_your_results_and_recent_performance' /> },
  { step: 3, label: <T id='Copy.r260127.If_you_re_selected_you_ll_receive_a_confirmation_email' /> },
  {
    step: 4,
    label: <T id='Copy.r260129.Your_profile_will_appear_in_the_master_trader_list_shortly_after_activation_copy' />,
  },
];

export const MasterTraderApplyForm = () => {
  const t = useTranslations();
  const router = useRouter();
  const { isLoggedInAtom } = useBunja(authBunja);
  const {
    nameAtom,
    socialMediaLinksAtom,
    agreeToTermsAndPolicyAtom,
    agreeToCopyTradingTermsAtom,
    profitSharePctAtom,
    isProfitShareEditableAtom,
    visibilityAtom,
    entryCodeAtom,
    entryCodeErrorAtom,
    isFormValidAtom,
    isLoadingAtom,
    submitForm,
    enableProfitShareEdit,
    resetProfitShareToRecommended,
  } = useBunja(applyMasterTraderBunja);

  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const [name, setName] = useAtom(nameAtom);
  const [socialMediaLinks, setSocialMediaLinks] = useAtom(socialMediaLinksAtom);
  const [agreeToTermsAndPolicy, setAgreeToTermsAndPolicy] = useAtom(agreeToTermsAndPolicyAtom);
  const [agreeToCopyTradingTerms, setAgreeToCopyTradingTerms] = useAtom(agreeToCopyTradingTermsAtom);
  const [profitSharePct, setProfitSharePct] = useAtom(profitSharePctAtom);
  const isProfitShareEditable = useAtomValue(isProfitShareEditableAtom);
  const [visibility, setVisibility] = useAtom(visibilityAtom);
  const [entryCode, setEntryCode] = useAtom(entryCodeAtom);
  const entryCodeError = useAtomValue(entryCodeErrorAtom);
  const isFormValid = useAtomValue(isFormValidAtom);
  const isLoading = useAtomValue(isLoadingAtom);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitForm();
  };

  const openLoginDialog = () => {
    dialog.open({
      title: { label: <T id='Copy.r260828.Log_in_to_apply_title' /> },
      content: <T id='Copy.r260828.Log_in_to_apply_description' />,
      buttons: {
        primary: (
          <Dialog.Button
            hierarchy='primary'
            label={<T id='Common.r250918.Got_it' />}
            onClick={() => router.push('/login')}
          />
        ),
      },
    });
  };

  const authGuard = () => {
    if (!isLoggedIn) openLoginDialog();
  };

  return (
    <section
      id='apply-form-section'
      className={css({
        position: 'relative',
        w: '100%',
        overflow: 'hidden',
      })}
    >
      <div className={css(radialGradientCss, { left: 'calc(-315px / 2)' })} />
      <div className={css(radialGradientCss, { right: 'calc(-315px / 2)' })} />
      <div
        className={vstack({
          position: 'relative',
          w: '100%',
          maxW: 600,
          gap: 40,
          mx: 'auto',
          py: { base: 60, tablet: 120, smallDesktop: 120 },
          pb: { base: 60, tablet: 200, smallDesktop: 200 },
          px: { base: 20, tablet: 40, smallDesktop: 0 },
        })}
      >
        <h1 className={css({ textStyle: 'Display/L', color: 'inverse.white', textAlign: 'center' })}>
          <T id='Copy.r260127.Apply_for_master_trader' />
        </h1>
        <div className={formCardWrapper}>
          <div className={formCardInner}>
            <form onSubmit={handleSubmit} className={vstack({ gap: 24, w: '100%' })}>
              <div className={vstack({ gap: 40 })}>
                <div className={stack({ gap: 8, w: '100%' })}>
                  <p className={requiredFormLabel}>
                    <T id='Common.r250923.Name' />
                  </p>
                  <Input
                    placeholder={t('Copy.r260127.Enter_first_and_last_name')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onClick={authGuard}
                    required
                  />
                </div>
                <div className={stack({ gap: 8, w: '100%' })}>
                  <p className={requiredFormLabel}>
                    <T id='Copy.r260127.Social_media_links' />
                  </p>
                  <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-1' })}>
                    <T id='Copy.r260129.E_g_telegram_whatsapp_x_instagram' />
                  </p>
                  <Input
                    placeholder={t('Copy.r260129.Paste_your_profile_link')}
                    value={socialMediaLinks}
                    onChange={(e) => setSocialMediaLinks(e.target.value)}
                    onClick={authGuard}
                  />
                </div>
                {/* Profit Share & Volume Rebate */}
                <div
                  className={grid({
                    gap: 8,
                    tabletDown: {
                      gridTemplateAreas: `
                      "ps"
                      "ps-setting"
                      "vr"
                    `,
                      gridTemplateColumns: '1fr',
                    },
                    tablet: {
                      gridTemplateAreas: `
                      "ps vr"
                      "ps-setting ps-setting"
                    `,
                      gridTemplateColumns: '1fr 1fr',
                    },
                  })}
                >
                  <div className={stack({ gridArea: 'ps', gap: 8 })}>
                    <p className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-5' })}>
                      <T id='Copy.r260127.Profit_share' />
                    </p>
                    <div
                      className={hstack({
                        gap: 8,
                        alignItems: 'center',
                        borderColor: 'border.interaction.disabled',
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        rounded: 8,
                        px: 16,
                        py: 12,
                        pr: 10,
                        textStyle: 'Text/L/Monospace/Regular',
                        color: isProfitShareEditable ? 'inverse.white' : 'content.default.level-1',
                        _focusWithin: { borderColor: 'border.default.level-4' },
                        transition: 'border-color 0.2s',
                      })}
                    >
                      <NumberInputWithSuffix
                        value={profitSharePct}
                        onChange={setProfitSharePct}
                        precision={0}
                        max='99'
                        placeholder='0% ~ 99%'
                        suffix={profitSharePct ? <span>%</span> : <wbr />}
                        disabled={!isProfitShareEditable}
                      />
                      {!isProfitShareEditable && (
                        <p
                          className={css({
                            textStyle: 'Text/M/Regular',
                            color: 'content.default.level-1',
                            flexShrink: 0,
                          })}
                        >
                          <T id='Copy.r260312.Recommended' />
                        </p>
                      )}
                    </div>
                  </div>
                  <div className={stack({ gridArea: 'vr', gap: 8, tabletDown: { pt: 24 - 8 } })}>
                    <p className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-5' })}>
                      <T id='Copy.r260312.Volume_rebate' />
                    </p>
                    <div
                      className={hstack({
                        gap: 8,
                        alignItems: 'center',
                        justify: 'space-between',
                        bg: 'background.default.level-3',
                        rounded: 8,
                        px: 16,
                        pr: 10,
                        py: 12,
                      })}
                    >
                      <Formatted.Percent
                        value={5}
                        precision={0}
                        css={{ textStyle: 'Text/L/Monospace/Regular', color: 'content.default.level-3' }}
                      />
                      <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-1' })}>
                        <T id='Copy.r260312.Fixed' />
                      </p>
                    </div>
                  </div>
                  <p
                    className={css({
                      gridArea: 'ps-setting',
                      textStyle: 'Text/L/Regular',
                      color: 'content.default.level-2',
                    })}
                  >
                    <T id='Copy.r260312.Changes_to_profit_share_settings_are_subject_to' />{' '}
                    <TextButton
                      as='button'
                      size='large'
                      label={
                        <T id={isProfitShareEditable ? 'Copy.r260312.Reset_to_recommended' : 'Copy.r260312.Change'} />
                      }
                      onClick={isProfitShareEditable ? resetProfitShareToRecommended : enableProfitShareEdit}
                    />
                  </p>
                </div>

                {/* Master Visibility */}
                <div className={stack({ gap: 12, w: '100%', textStyle: 'Text/M/Regular' })}>
                  <p className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-5' })}>
                    <T id='Copy.r260312.Master_visibility' />
                  </p>
                  <div className={stack({ gap: 8 })}>
                    <div className={stack({ gap: 4, textStyle: 'Text/L/Regular' })}>
                      <RadioButton
                        name='visibility'
                        value='PUBLIC'
                        checked={visibility === 'PUBLIC'}
                        onChange={() => setVisibility('PUBLIC')}
                      >
                        <p className={stack({ direction: 'row', gap: 8 })}>
                          <span className={css({ color: 'content.default.level-5' })}>
                            <T id='Copy.r260312.Public_button' />
                          </span>
                          <span className={css({ color: 'content.default.level-2', tabletDown: { display: 'none' } })}>
                            <T id='Copy.r260312.Anyone_can_copy' />
                          </span>
                        </p>
                      </RadioButton>
                      <p className={css({ color: 'content.default.level-2', tablet: { display: 'none' } })}>
                        <T id='Copy.r260312.Anyone_can_copy' />
                      </p>
                    </div>
                    <div className={stack({ gap: 4 })}>
                      <RadioButton
                        name='visibility'
                        value='PRIVATE'
                        checked={visibility === 'PRIVATE'}
                        onChange={() => setVisibility('PRIVATE')}
                      >
                        <p className={stack({ direction: 'row', gap: 8 })}>
                          <span className={css({ color: 'content.default.level-5' })}>
                            <T id='Copy.r260312.Private_button' />
                          </span>
                          <span className={css({ color: 'content.default.level-2', tabletDown: { display: 'none' } })}>
                            <T id='Copy.r260312.Only_users_with_the_entry_code_can_copy' />
                          </span>
                        </p>
                      </RadioButton>
                      <p className={css({ color: 'content.default.level-2', tablet: { display: 'none' } })}>
                        <T id='Copy.r260312.Only_users_with_the_entry_code_can_copy' />
                      </p>
                    </div>
                  </div>
                  {visibility === 'PRIVATE' && (
                    <>
                      <div
                        className={hstack({
                          gap: 8,
                          alignItems: 'center',
                          borderColor: entryCodeError ? 'border.accent.negative' : 'border.interaction.disabled',
                          borderWidth: '1px',
                          borderStyle: 'solid',
                          rounded: 8,
                          px: 16,
                          py: 12,
                          pr: 10,
                          textStyle: 'Text/L/Monospace/Regular',
                          color: 'inverse.white',
                          _focusWithin: {
                            borderColor: entryCodeError ? 'border.accent.negative' : 'border.default.level-4',
                          },
                          transition: 'border-color 0.2s',
                        })}
                      >
                        <input
                          className={css({
                            w: '100%',
                            bg: 'transparent',
                            outline: 'none',
                            border: 'none',
                            color: 'inherit',
                            textStyle: 'inherit',
                            _placeholder: { color: 'content.default.level-1' },
                          })}
                          placeholder={t('Copy.r260312.Enter_4_to_12_letters_or_numbers')}
                          value={entryCode}
                          onChange={(e) => setEntryCode(e.target.value.toUpperCase())}
                        />
                        {entryCode && (
                          <button
                            className={css({ flexShrink: 0, color: 'content.default.level-3' })}
                            onClick={() => setEntryCode('')}
                          >
                            <XCircleIcon size={20} />
                          </button>
                        )}
                      </div>
                      {entryCodeError === 'special-characters' && (
                        <Explain.Error>
                          <T id='Copy.r260312.Special_characters_are_not_allowed' />
                        </Explain.Error>
                      )}
                      {entryCodeError === 'length' && (
                        <Explain.Error>
                          <T id='Copy.r260312.Enter_4_to_12_letters_or_numbers' />
                        </Explain.Error>
                      )}
                    </>
                  )}
                </div>

                <div className={css({ w: '100%', h: '1px', bg: 'border.default.level-1' })} />

                <div className={stack({ gap: 12, w: '100%' })}>
                  <Checkbox
                    checked={agreeToTermsAndPolicy}
                    onChange={(e) => setAgreeToTermsAndPolicy(e.target.checked)}
                    disabled={!isLoggedIn}
                  >
                    <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
                      <T
                        id='Copy.r260127.I_have_read_and_agree_to_terms_and_private_policy'
                        values={{
                          a1: (chunks: React.ReactNode) => (
                            <a href='/policies/terms' className={css({ color: 'content.accent.info' })}>
                              {chunks}
                            </a>
                          ),
                          a2: (chunks: React.ReactNode) => (
                            <a href='/policies/privacy' className={css({ color: 'content.accent.info' })}>
                              {chunks}
                            </a>
                          ),
                        }}
                      />
                    </span>
                  </Checkbox>
                  <Checkbox
                    checked={agreeToCopyTradingTerms}
                    onChange={(e) => setAgreeToCopyTradingTerms(e.target.checked)}
                    disabled={!isLoggedIn}
                  >
                    <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
                      <T
                        id='Copy.r260127.I_have_read_and_agree_to_the_copy_trading_terms_the_master_trader_terms_and_the'
                        values={{
                          a1: (chunks: React.ReactNode) => (
                            <a
                              href='/policies/copy-trading-specific-terms'
                              className={css({ color: 'content.accent.info' })}
                            >
                              {chunks}
                            </a>
                          ),
                          a2: (chunks: React.ReactNode) => (
                            <a
                              href='/policies/master-trader-specific-terms'
                              className={css({ color: 'content.accent.info' })}
                            >
                              {chunks}
                            </a>
                          ),
                        }}
                      />
                    </span>
                  </Checkbox>
                </div>
              </div>
              {isLoggedIn ? (
                <Button
                  as='button'
                  htmlType='submit'
                  label={<T id='Vip.r260121.Submit' />}
                  size='large'
                  css={{ w: '100%' }}
                  isLoading={isLoading}
                  disabled={!isFormValid}
                />
              ) : (
                <Button
                  as='a'
                  href='/login'
                  label={<T id='Copy.r260127.Log_in_to_apply_button' />}
                  size='large'
                  css={{ w: '100%' }}
                />
              )}
            </form>
          </div>
        </div>

        <div className={stack({ gap: 24, w: '100%' })}>
          <p className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-3' })}>
            <T id='Copy.r260127.Eligibility_next_steps' />
          </p>
          <div className={stack({ gap: 16 })}>
            {steps.map((step) => (
              <div key={step.step} className={hstack({ gap: 16, alignItems: 'flex-start' })}>
                <div
                  className={css({
                    minW: 24,
                    h: 24,
                    bg: 'background.default.level-3',
                    rounded: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textStyle: 'Text/M/Monospace/Strong',
                    color: 'content.default.level-2',
                  })}
                >
                  {step.step}
                </div>
                <p
                  className={css({
                    textStyle: 'Text/M/Regular',
                    color: 'content.default.level-3',
                    whiteSpace: 'pre-line',
                  })}
                >
                  {step.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const radialGradientCss = css.raw({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  w: 315,
  h: 315,
  borderRadius: '50%',
  pointerEvents: 'none',
  background:
    'radial-gradient(circle, token(colors.background.accent.primary.tint) 0%, token(colors.background.accent.primary.tint/0) 70%)',
});

// Gradient border wrapper
const formCardWrapper = css({
  w: '100%',
  p: '1px',
  rounded: 16,
  background:
    'linear-gradient(180deg, token(colors.content.accent.info) 0%, token(colors.border.accent.info) 50%, token(colors.background.default.level-0) 100%)',
});

// Inner content with solid background
const formCardInner = css({
  gap: 24,
  p: { base: 20, tablet: 32 },
  rounded: 15,
  w: '100%',
  bg: 'background.default.level-0',
});

const requiredFormLabel = css({
  textStyle: 'Text/L/Strong',
  color: 'content.default.level-5',
});
